/** First-party marketing pageviews → Razzmatazz web analytics (gateway ingest). */

const SESSION_KEY = 'nextcompli.site.traffic';
const VISITOR_KEY = 'nextcompli.site.visitor';
const UTM_KEY = 'nextcompli.site.utm';
/** Local opt-out after Traffic → Exclude my device (also enforced server-side). */
const EXCLUDE_KEY = 'nextcompli.site.traffic.exclude';

/** Visible-time heartbeat while the tab is active (does not create new pageviews). */
const DWELL_HEARTBEAT_MS = 15_000;

type StoredUtm = {
  utm_source?: string;
  utm_medium?: string;
  utm_campaign?: string;
  utm_content?: string;
  utm_term?: string;
};

type DwellState = {
  path: string;
  startedAt: number;
  visibleAccumMs: number;
  lastVisibleAt: number | null;
  heartbeatId: number | null;
};

let dwell: DwellState | null = null;

const inferGatewayBaseUrl = (): string => {
  const configured = (process.env.REACT_APP_GATEWAY_URL || '').trim();
  if (configured) {
    return configured.replace(/\/$/, '').replace(/localhost/i, '127.0.0.1');
  }
  if (typeof window === 'undefined') {
    return 'http://127.0.0.1:8000';
  }
  const host = window.location.hostname;
  if (host === 'localhost' || host === '127.0.0.1' || host === '::1') {
    return 'http://127.0.0.1:8000';
  }
  return `https://${host.replace(/^www\./, '')}`;
};

const sessionId = (): string => {
  try {
    const existing = window.sessionStorage.getItem(SESSION_KEY) || '';
    if (existing.length >= 8) {
      return existing;
    }
    const next = window.crypto?.randomUUID?.() || `pv-${Date.now().toString(36)}`;
    window.sessionStorage.setItem(SESSION_KEY, next);
    return next;
  } catch {
    return `pv-${Date.now().toString(36)}`;
  }
};

const visitorId = (): string => {
  try {
    const existing = window.localStorage.getItem(VISITOR_KEY) || '';
    if (existing.length >= 8) {
      return existing;
    }
    const next = window.crypto?.randomUUID?.() || `vid-${Date.now().toString(36)}`;
    window.localStorage.setItem(VISITOR_KEY, next);
    return next;
  } catch {
    return sessionId();
  }
};

/** Stable anonymous visitor id (also used to exclude operator test devices). */
export function getSiteVisitorId(): string {
  if (typeof window === 'undefined') {
    return '';
  }
  return visitorId();
}

export function isSiteTrafficExcludedLocally(): boolean {
  if (typeof window === 'undefined') {
    return false;
  }
  try {
    return window.localStorage.getItem(EXCLUDE_KEY) === '1';
  } catch {
    return false;
  }
}

/** Mark this browser as an operator test device (skips beacons until cleared). */
export function setSiteTrafficExcludedLocally(excluded: boolean): void {
  if (typeof window === 'undefined') {
    return;
  }
  try {
    if (excluded) {
      window.localStorage.setItem(EXCLUDE_KEY, '1');
    } else {
      window.localStorage.removeItem(EXCLUDE_KEY);
    }
  } catch {
    /* ignore */
  }
}

const readQueryUtm = (): StoredUtm => {
  if (typeof window === 'undefined') {
    return {};
  }
  const params = new URLSearchParams(window.location.search || '');
  const next: StoredUtm = {};
  (['utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term'] as const).forEach((key) => {
    const value = (params.get(key) || '').trim();
    if (value) {
      next[key] = value.slice(0, 180);
    }
  });
  return next;
};

/** Keep first-touch UTM for the browser session when a campaign lands. */
const sessionUtm = (): StoredUtm => {
  const fromQuery = readQueryUtm();
  try {
    const existingRaw = window.sessionStorage.getItem(UTM_KEY);
    if (existingRaw) {
      const stored = JSON.parse(existingRaw) as StoredUtm;
      if (stored && typeof stored === 'object' && Object.keys(stored).length > 0) {
        return stored;
      }
    }
    if (Object.keys(fromQuery).length > 0) {
      window.sessionStorage.setItem(UTM_KEY, JSON.stringify(fromQuery));
      return fromQuery;
    }
  } catch {
    /* ignore */
  }
  return fromQuery;
};

const isOperatorShell = (path: string): boolean => (
  path.startsWith('/console')
  || path.startsWith('/embed/')
  || path === '/embed'
);

const postJson = (url: string, body: string): void => {
  try {
    if (navigator.sendBeacon) {
      const blob = new Blob([body], { type: 'application/json' });
      if (navigator.sendBeacon(url, blob)) {
        return;
      }
    }
  } catch {
    /* fetch below */
  }
  void fetch(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body,
    keepalive: true,
  }).catch(() => undefined);
};

const accumulateVisible = (): void => {
  if (!dwell || dwell.lastVisibleAt == null) return;
  const now = Date.now();
  dwell.visibleAccumMs += Math.max(0, now - dwell.lastVisibleAt);
  dwell.lastVisibleAt = document.visibilityState === 'visible' ? now : null;
};

const currentDwellMs = (): number => {
  if (!dwell) return 0;
  accumulateVisible();
  return Math.max(0, Math.round(dwell.visibleAccumMs));
};

const flushDwell = (reason: 'heartbeat' | 'leave' | 'hidden'): void => {
  if (typeof window === 'undefined' || !dwell) return;
  if (isSiteTrafficExcludedLocally()) {
    return;
  }
  if (process.env.NODE_ENV === 'test' && process.env.REACT_APP_SITE_TRAFFIC !== '1') {
    return;
  }
  const ms = currentDwellMs();
  if (ms < 500 && reason !== 'leave') {
    return;
  }
  const body = JSON.stringify({
    path: dwell.path,
    session_id: sessionId(),
    visitor_id: visitorId(),
    host: window.location.host,
    dwell_ms: ms,
    reason,
  });
  postJson(`${inferGatewayBaseUrl()}/api/v1/site-traffic/dwell`, body);
};

const stopHeartbeat = (): void => {
  if (dwell?.heartbeatId != null) {
    window.clearInterval(dwell.heartbeatId);
    dwell.heartbeatId = null;
  }
};

const startHeartbeat = (): void => {
  if (!dwell || dwell.heartbeatId != null) return;
  dwell.heartbeatId = window.setInterval(() => {
    if (document.visibilityState !== 'visible') return;
    flushDwell('heartbeat');
  }, DWELL_HEARTBEAT_MS);
};

const beginDwell = (path: string): void => {
  const resolved = (path || '/').split('?')[0];
  if (isOperatorShell(resolved)) {
    stopHeartbeat();
    dwell = null;
    return;
  }
  if (dwell && dwell.path === resolved) {
    if (document.visibilityState === 'visible' && dwell.lastVisibleAt == null) {
      dwell.lastVisibleAt = Date.now();
    }
    startHeartbeat();
    return;
  }
  if (dwell) {
    flushDwell('leave');
    stopHeartbeat();
  }
  const now = Date.now();
  dwell = {
    path: resolved,
    startedAt: now,
    visibleAccumMs: 0,
    lastVisibleAt: document.visibilityState === 'visible' ? now : null,
    heartbeatId: null,
  };
  startHeartbeat();
};

/** Record a public marketing path. Skips /console so operator work is not counted as traffic. */
export function noteSitePageview(path?: string): void {
  if (typeof window === 'undefined') {
    return;
  }
  if (isSiteTrafficExcludedLocally()) {
    return;
  }
  if (process.env.NODE_ENV === 'test' && process.env.REACT_APP_SITE_TRAFFIC !== '1') {
    return;
  }
  const resolved = (path || window.location.pathname || '/').split('?')[0];
  // Operator shells are not marketing traffic (Matomo/Plausible: exclude private apps).
  if (isOperatorShell(resolved)) {
    beginDwell(resolved);
    return;
  }
  const utm = sessionUtm();
  const body = JSON.stringify({
    path: resolved,
    referrer: document.referrer || '',
    session_id: sessionId(),
    visitor_id: visitorId(),
    host: window.location.host,
    title: (document.title || '').slice(0, 240),
    language: (navigator.language || '').slice(0, 40),
    screen_width: window.screen?.width || window.innerWidth || null,
    user_agent: navigator.userAgent || '',
    ...utm,
  });
  postJson(`${inferGatewayBaseUrl()}/api/v1/site-traffic/pageview`, body);
  beginDwell(resolved);
}

const interactionTargetLabel = (node: Element | null): { target: string; label: string } => {
  if (!node || !(node instanceof Element)) {
    return { target: '', label: '' };
  }
  const el = node.closest('a,button,[role="button"],input,select,textarea,[data-track]') as HTMLElement | null;
  if (!el) {
    return { target: '', label: '' };
  }
  const target = (
    el.getAttribute('data-track')
    || el.id
    || el.getAttribute('name')
    || el.getAttribute('aria-label')
    || el.tagName.toLowerCase()
  ).slice(0, 120);
  const label = (el.textContent || el.getAttribute('aria-label') || '').replace(/\s+/g, ' ').trim().slice(0, 120);
  return { target, label };
};

const noteInteraction = (
  eventType: 'click' | 'hover',
  detail: { target?: string; label?: string; x?: number; y?: number; duration_ms?: number },
): void => {
  if (typeof window === 'undefined' || isSiteTrafficExcludedLocally()) return;
  if (process.env.NODE_ENV === 'test' && process.env.REACT_APP_SITE_TRAFFIC !== '1') return;
  const path = (window.location.pathname || '/').split('?')[0];
  if (isOperatorShell(path)) return;
  postJson(`${inferGatewayBaseUrl()}/api/v1/site-traffic/interaction`, {
    event_type: eventType,
    path,
    host: window.location.host,
    session_id: sessionId(),
    visitor_id: visitorId(),
    target: detail.target || '',
    label: detail.label || '',
    x: detail.x ?? null,
    y: detail.y ?? null,
    duration_ms: detail.duration_ms || 0,
  });
};

export function installSiteTraffic(): void {
  if (typeof window === 'undefined') {
    return;
  }
  noteSitePageview();
  window.addEventListener('popstate', () => noteSitePageview());
  const historyApi = window.history;
  const originalPush = historyApi.pushState.bind(historyApi);
  historyApi.pushState = (...args) => {
    originalPush(...args);
    noteSitePageview();
  };
  const originalReplace = historyApi.replaceState.bind(historyApi);
  historyApi.replaceState = (...args) => {
    originalReplace(...args);
    noteSitePageview();
  };
  document.addEventListener('visibilitychange', () => {
    if (!dwell) return;
    if (document.visibilityState === 'hidden') {
      flushDwell('hidden');
      stopHeartbeat();
      if (dwell.lastVisibleAt != null) {
        accumulateVisible();
        dwell.lastVisibleAt = null;
      }
      return;
    }
    dwell.lastVisibleAt = Date.now();
    startHeartbeat();
  });
  window.addEventListener('pagehide', () => {
    flushDwell('leave');
    stopHeartbeat();
  });

  // Lightweight timeline markers (not full session replay). Privacy: no DOM snapshots.
  document.addEventListener(
    'click',
    (event) => {
      const { target, label } = interactionTargetLabel(event.target as Element | null);
      if (!target && !label) return;
      noteInteraction('click', {
        target,
        label,
        x: event.clientX,
        y: event.clientY,
      });
    },
    { capture: true, passive: true },
  );
  let hoverTimer: number | null = null;
  let hoverStarted: { target: string; label: string; at: number } | null = null;
  document.addEventListener(
    'mouseover',
    (event) => {
      const { target, label } = interactionTargetLabel(event.target as Element | null);
      if (!target && !label) return;
      if (hoverTimer != null) window.clearTimeout(hoverTimer);
      hoverStarted = { target, label, at: Date.now() };
      hoverTimer = window.setTimeout(() => {
        if (!hoverStarted) return;
        noteInteraction('hover', {
          target: hoverStarted.target,
          label: hoverStarted.label,
          duration_ms: Date.now() - hoverStarted.at,
        });
      }, 900);
    },
    { capture: true, passive: true },
  );
  document.addEventListener(
    'mouseout',
    () => {
      if (hoverTimer != null) window.clearTimeout(hoverTimer);
      hoverTimer = null;
      hoverStarted = null;
    },
    { capture: true, passive: true },
  );
}

/** First-party identity only — call after the visitor submits email/name (signup, subscribe, contact). */
export function identifySiteVisitor(input: {
  email?: string;
  full_name?: string;
  company_name?: string;
  source?: string;
  path?: string;
}): void {
  if (typeof window === 'undefined') {
    return;
  }
  if (process.env.NODE_ENV === 'test' && process.env.REACT_APP_SITE_TRAFFIC !== '1') {
    return;
  }
  const email = (input.email || '').trim();
  const fullName = (input.full_name || '').trim();
  if (!email && !fullName) {
    return;
  }
  postJson(`${inferGatewayBaseUrl()}/api/v1/site-traffic/identify`, {
    visitor_id: visitorId(),
    session_id: sessionId(),
    host: window.location.host,
    path: (input.path || window.location.pathname || '/').split('?')[0],
    email,
    full_name: fullName,
    company_name: (input.company_name || '').trim(),
    source: (input.source || 'form').trim() || 'form',
  });
}
